/**
 * What true-up lines up: the features of separately scanned rooms that should
 * coincide exactly, found once from the rooms as placed, and how far each is
 * from holding as the rooms are edited.
 */
import type { Point } from '@kaiizen/kai-floor-plan';
import { acrossOn, add, alongOn, dot, overlapOf, parallel, pointAt, type SnapLine } from '../snapLines';

/** Features this far apart (ft) or closer are taken to be the same feature. */
export const TRUE_UP_MATCH_DISTANCE = 3 / 12;
/** How close (ft) another room must come to a wall's outer face for the wall to be inside the property: a shared wall, give or take. */
const NEIGHBOR_DISTANCE = 2 * TRUE_UP_MATCH_DISTANCE;

/**
 * One room's wall face as it was when true-up started. Edits change a room's
 * walls, and can split one straight run into two (a closet that stops a hair
 * short of the side wall), so a face is found again by where it was, moved
 * with its room, rather than by its place in `roomSnapLines`.
 */
interface Face {
  room: number;
  line: SnapLine;
}

/**
 * Something that should hold exactly: two rooms' faces coinciding (a shared
 * or flush wall), a doorway edge seen from both sides, two rooms' outside
 * walls in one line, or one room's straight run staying straight.
 */
export type Match =
  | { kind: 'wall'; faces: [Face, Face] }
  | { kind: 'inLine'; faces: [Face, Face] }
  | { kind: 'doorway'; faces: [Face, Face]; openings: [number, number] }
  | { kind: 'straight'; face: Face };

/** Every match but a straight run: the features true-up lines up and reports. */
export const isFeatureMatch = (match: Match): boolean => match.kind !== 'straight';

/** The room's current faces that lie along the face's original line, in order along it. */
function piecesOf(lines: SnapLine[][], shifts: Point[], face: Face): SnapLine[] {
  const reference = { ...face.line, origin: add(face.line.origin, shifts[face.room]) };
  return lines[face.room]
    .filter(line =>
      line.face === reference.face && dot(line.dir, reference.dir) > 0 && parallel(line, reference)
      && Math.abs(acrossOn(reference, line.origin)) <= TRUE_UP_MATCH_DISTANCE && overlapOf(reference, line) > 0
    )
    .sort((a, b) => alongOn(reference, a.origin) - alongOn(reference, b.origin));
}

/** A doorway edge where the face's original opening edge `index` now is: the nearest current one along the face. */
function openingPoint(lines: SnapLine[][], shifts: Point[], face: Face, index: number): Point | null {
  const target = add(pointAt(face.line, face.line.openings[index]), shifts[face.room]);
  let best: Point | null = null, bestDistance = TRUE_UP_MATCH_DISTANCE;
  for (const piece of piecesOf(lines, shifts, face)) {
    for (const along of piece.openings) {
      const point = pointAt(piece, along), distance = Math.hypot(point.x - target.x, point.y - target.y);
      if (distance <= bestDistance) [best, bestDistance] = [point, distance];
    }
  }
  return best;
}

/** The pair of pieces, one on each face, that overlap the most. */
function facingPieces(lines: SnapLine[][], shifts: Point[], [myFace, theirFace]: [Face, Face]): [SnapLine, SnapLine] | null {
  let best: [SnapLine, SnapLine] | null = null, bestOverlap = 0;
  for (const mine of piecesOf(lines, shifts, myFace)) {
    for (const theirs of piecesOf(lines, shifts, theirFace)) {
      const overlap = overlapOf(mine, theirs);
      if (overlap > bestOverlap) [best, bestOverlap] = [[mine, theirs], overlap];
    }
  }
  return best;
}

/**
 * How far a match is from holding (ft, signed): across the faces, along them
 * for a doorway edge, or the step in a straight run. `lines` are each room's
 * current faces and `shifts` how far each room has moved since true-up started.
 */
export function residual(lines: SnapLine[][], shifts: Point[], match: Match): number {
  if (match.kind === 'straight') {
    const pieces = piecesOf(lines, shifts, match.face);
    return pieces.length > 1 ? acrossOn(pieces[0], pieces[pieces.length - 1].origin) : 0;
  }
  if (match.kind === 'wall') {
    const pair = facingPieces(lines, shifts, match.faces);
    return pair ? acrossOn(pair[0], pair[1].origin) : 0;
  }
  if (match.kind === 'inLine') {
    const [mine] = piecesOf(lines, shifts, match.faces[0]), [theirs] = piecesOf(lines, shifts, match.faces[1]);
    return mine && theirs ? acrossOn(mine, theirs.origin) : 0;
  }
  const [myFace, theirFace] = match.faces;
  const mine = openingPoint(lines, shifts, myFace, match.openings[0]), theirs = openingPoint(lines, shifts, theirFace, match.openings[1]);
  return mine && theirs ? dot({ x: theirs.x - mine.x, y: theirs.y - mine.y }, myFace.line.dir) : 0;
}

/** Faces that may coincide: facing walls share a wall (one outer face on the other's inner), walls facing the same way run flush. */
const mayCoincide = (line: SnapLine, partner: SnapLine) =>
  dot(line.perp, partner.perp) < 0 ? line.face === 'outer' && partner.face === 'inner' : line.face === 'inner' && partner.face === 'inner';

/**
 * The doorways along a shared or flush wall, one match per edge: an opening on
 * each face whose two edges each lie within the match distance of the other's.
 */
function doorwayMatches(faces: [Face, Face]): Match[] {
  const [{ line }, { line: partner }] = faces;
  const matches: Match[] = [];
  for (let myEdge = 0; myEdge + 1 < line.openings.length; myEdge += 2) {
    const [start, end] = [line.openings[myEdge], line.openings[myEdge + 1]];
    for (let theirEdge = 0; theirEdge + 1 < partner.openings.length; theirEdge += 2) {
      const theirAlong = [theirEdge, theirEdge + 1].map(edge => alongOn(line, pointAt(partner, partner.openings[edge])));
      const [low, high] = theirAlong[0] <= theirAlong[1] ? [theirEdge, theirEdge + 1] : [theirEdge + 1, theirEdge];
      const [from, to] = [Math.min(...theirAlong), Math.max(...theirAlong)];
      if (Math.abs(from - start) > TRUE_UP_MATCH_DISTANCE || Math.abs(to - end) > TRUE_UP_MATCH_DISTANCE) continue;
      matches.push({ kind: 'doorway', faces, openings: [myEdge, low] }, { kind: 'doorway', faces, openings: [myEdge + 1, high] });
    }
  }
  return matches;
}

/** Everything that should hold among rooms whose current faces are `lines`, one list per room. */
export function findMatches(lines: SnapLine[][]): Match[] {
  const matches: Match[] = lines.flatMap((roomLines, room) =>
    roomLines.filter(line => line.face === 'inner').map(line => ({ kind: 'straight' as const, face: { room, line } }))
  );
  for (let room = 0; room < lines.length; room++) {
    for (let other = room + 1; other < lines.length; other++) {
      for (const line of lines[room]) {
        for (const partner of lines[other]) {
          if (!parallel(line, partner) || !mayCoincide(line, partner)) continue;
          if (overlapOf(line, partner) <= 0 || Math.abs(acrossOn(line, partner.origin)) > TRUE_UP_MATCH_DISTANCE) continue;
          const faces: [Face, Face] = [{ room, line }, { room: other, line: partner }];
          matches.push({ kind: 'wall', faces }, ...doorwayMatches(faces));
        }
      }
    }
  }
  return [...matches, ...outsideWallsInLine(lines)];
}

/** Whether two lines run within a degree of parallel with their normals the same way (`1`) or opposite ways (`-1`). */
const parallelFacing = (a: SnapLine, b: SnapLine, way: 1 | -1): boolean => parallel(a, b) && Math.sign(dot(a.perp, b.perp)) === way;

/** How much (ft) of a wall's outer face other rooms' inner faces lie along, facing it about a shared wall away. */
const neighboredLength = (lines: SnapLine[][], room: number, outer: SnapLine): number =>
  lines.reduce((sum, others, other) => {
    if (other === room) return sum;
    return sum + others.reduce((covered, partner) => {
      const facingIt = partner.face === 'inner' && parallelFacing(outer, partner, -1);
      const near = facingIt && Math.abs(acrossOn(outer, partner.origin)) <= NEIGHBOR_DISTANCE;
      return near ? covered + Math.max(0, overlapOf(outer, partner)) : covered;
    }, 0);
  }, 0);

/**
 * Each room's outside walls, by their inner faces: those with no other room
 * along most of the outer face. `roomSnapLines` lists each wall's inner face,
 * then its outer face.
 */
const outsideFaces = (lines: SnapLine[][]): Face[] =>
  lines.flatMap((roomLines, room) =>
    roomLines.flatMap((line, index) => line.face === 'inner' && neighboredLength(lines, room, roomLines[index + 1]) < line.length / 2 ? [{ room, line }] : [])
  );

/**
 * Outside walls of different rooms that run nearly in line, pulled into one
 * line: the property's outline has no small jogs a scan put there. Each
 * group of walls facing the same way, within the match distance of the
 * group's first, lines up on that first.
 */
function outsideWallsInLine(lines: SnapLine[][]): Match[] {
  const outside = outsideFaces(lines);
  const matches: Match[] = [];
  const grouped = new Set<Face>();
  for (const first of outside) {
    if (grouped.has(first)) continue;
    grouped.add(first);
    for (const face of outside) {
      if (grouped.has(face) || face.room === first.room || !parallelFacing(face.line, first.line, 1)) continue;
      if (Math.abs(acrossOn(first.line, face.line.origin)) > TRUE_UP_MATCH_DISTANCE) continue;
      grouped.add(face);
      matches.push({ kind: 'inLine', faces: [first, face] });
    }
  }
  return matches;
}
